import { normalizeTrack } from '@/state/trackModel';

const setParam = (param, value, now = 0) => {
  if (!param) return;
  if (typeof param.setValueAtTime === 'function') param.setValueAtTime(value, now);
  else param.value = value;
};

// Smooth manual filter moves in live playback; offline export starts at the saved value.
const setFilterParam = (param, value, now, offline) => {
  if (!offline && typeof param.setTargetAtTime === 'function') {
    param.cancelScheduledValues(now);
    param.setTargetAtTime(value, now, 0.015);
  } else setParam(param, value, now);
};

export const dottedEighthSeconds = (bpm) => 45 / Math.max(1, Number(bpm) || 90);

const audible = (track, anySoloed) => !track.muted && (!anySoloed || track.solo);

function generatedImpulse(ctx, seconds = 2.2) {
  if (typeof ctx.createBuffer !== 'function') return null;
  const length = Math.max(1, Math.round((ctx.sampleRate || 44100) * seconds));
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate || 44100);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < length; index += 1) {
      const decay = Math.pow(1 - index / length, 2.5);
      data[index] = (Math.random() * 2 - 1) * decay;
    }
  }
  return buffer;
}

const disconnectAll = (nodes) => nodes.forEach((node) => {
  try { node.disconnect(); } catch (error) { /* A partially built graph can be disconnected safely. */ }
});

/** Build the persistent live or offline mix graph used by playback and export. */
export function buildMixGraph(ctx, { tracks = [], bpm = 90, destination = ctx.destination } = {}) {
  const offline = typeof ctx.startRendering === 'function';
  const strips = new Map();
  let currentBpm = Number(bpm) || 90;

  const masterInput = ctx.createGain();
  const limiter = ctx.createDynamicsCompressor();
  setParam(limiter.threshold, -1);
  setParam(limiter.knee, 0);
  setParam(limiter.ratio, 20);
  setParam(limiter.attack, 0.001);
  setParam(limiter.release, 0.08);
  const masterAnalyser = offline ? null : ctx.createAnalyser();
  if (masterAnalyser) { masterAnalyser.fftSize = 256; masterAnalyser.smoothingTimeConstant = 0.72; }
  masterInput.connect(limiter);
  limiter.connect(masterAnalyser || destination);
  if (masterAnalyser) masterAnalyser.connect(destination);

  const reverb = ctx.createConvolver();
  reverb.buffer = generatedImpulse(ctx);
  const reverbReturn = ctx.createGain();
  reverb.connect(reverbReturn);
  reverbReturn.connect(masterInput);

  const echoDelay = ctx.createDelay(4);
  const echoFeedback = ctx.createGain();
  const echoFilter = ctx.createBiquadFilter();
  const echoReturn = ctx.createGain();
  echoFilter.type = 'lowpass';
  setParam(echoFilter.frequency, 5000);
  setParam(echoFeedback.gain, 0.35);
  echoDelay.connect(echoReturn);
  echoReturn.connect(masterInput);
  echoDelay.connect(echoFeedback);
  echoFeedback.connect(echoFilter);
  echoFilter.connect(echoDelay);

  const sharedNodes = [masterInput, limiter, reverb, reverbReturn, echoDelay, echoFeedback, echoFilter, echoReturn, ...(masterAnalyser ? [masterAnalyser] : [])];

  const createStrip = (track) => {
    const input = ctx.createGain();
    const highpass = ctx.createBiquadFilter();
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    const low = ctx.createBiquadFilter();
    const mid = ctx.createBiquadFilter();
    const high = ctx.createBiquadFilter();
    highpass.type = 'highpass'; setParam(highpass.frequency, 20); setParam(highpass.Q, Math.SQRT1_2);
    low.type = 'lowshelf'; mid.type = 'peaking'; high.type = 'highshelf';
    setParam(low.frequency, 120); setParam(mid.frequency, 1000); setParam(mid.Q, 0.9); setParam(high.frequency, 8000);
    const punch = ctx.createDynamicsCompressor();
    const makeup = ctx.createGain();
    const gain = ctx.createGain();
    const pan = ctx.createStereoPanner();
    const analyser = offline ? null : ctx.createAnalyser();
    if (analyser) { analyser.fftSize = 256; analyser.smoothingTimeConstant = 0.68; }
    const reverbSend = ctx.createGain();
    const echoSend = ctx.createGain();

    input.connect(low); highpass.connect(low); low.connect(mid); mid.connect(high); high.connect(punch); lowpass.connect(punch); punch.connect(makeup); makeup.connect(gain); gain.connect(pan);
    const output = analyser || pan;
    if (analyser) pan.connect(analyser);
    output.connect(masterInput);
    output.connect(reverbSend); reverbSend.connect(reverb);
    output.connect(echoSend); echoSend.connect(echoDelay);

    const nodes = [input, highpass, lowpass, low, mid, high, punch, makeup, gain, pan, reverbSend, echoSend, ...(analyser ? [analyser] : [])];
    const strip = { input, highpassEnabled: false, lowpassEnabled: false, highpass, lowpass, low, mid, high, punch, makeup, gain, pan, analyser, reverbSend, echoSend, nodes };
    strips.set(track.id, strip);
    return strip;
  };

  const updateStrip = (strip, track, anySoloed) => {
    const clean = normalizeTrack(track);
    const now = Number(ctx.currentTime) || 0;
    const highpassEnabled = clean.effects.highPassHz > 0;
    if (strip.highpassEnabled !== highpassEnabled) {
      strip.input.disconnect();
      strip.input.connect(highpassEnabled ? strip.highpass : strip.low);
      strip.highpassEnabled = highpassEnabled;
    }
    setParam(strip.highpass.frequency, clean.effects.highPassHz || 20, now);
    const lowpassEnabled = clean.effects.lowPassHz > 0;
    if (strip.lowpassEnabled !== lowpassEnabled) {
      strip.high.disconnect();
      strip.high.connect(lowpassEnabled ? strip.lowpass : strip.punch);
      strip.lowpassEnabled = lowpassEnabled;
    }
    setFilterParam(strip.lowpass.frequency, Math.min(clean.effects.lowPassHz || 20000, (ctx.sampleRate || 44100) * 0.49), now, offline);
    // Biquad low-pass Q is in dB. Zero resonance starts with a gentle Butterworth response.
    setFilterParam(strip.lowpass.Q, -3.0103 + 15.0103 * clean.effects.lowPassResonance, now, offline);
    setParam(strip.low.gain, clean.effects.eq.low, now);
    setParam(strip.mid.gain, clean.effects.eq.mid, now);
    setParam(strip.high.gain, clean.effects.eq.high, now);
    const amount = clean.effects.punch;
    setParam(strip.punch.threshold, -24 * amount, now);
    setParam(strip.punch.knee, 0, now);
    setParam(strip.punch.ratio, 1 + 5 * amount, now);
    setParam(strip.punch.attack, 0.005, now);
    setParam(strip.punch.release, 0.12, now);
    setParam(strip.makeup.gain, Math.pow(10, (6 * amount) / 20), now);
    setParam(strip.gain.gain, audible(clean, anySoloed) ? clean.volume / 100 : 0, now);
    setParam(strip.pan.pan, clean.pan / 50, now);
    setParam(strip.reverbSend.gain, clean.effects.reverb, now);
    setParam(strip.echoSend.gain, clean.effects.echo, now);
  };

  const update = (nextTracks = [], nextBpm = currentBpm) => {
    currentBpm = Math.max(1, Number(nextBpm) || currentBpm);
    setParam(echoDelay.delayTime, dottedEighthSeconds(currentBpm), Number(ctx.currentTime) || 0);
    const normalized = nextTracks.map(normalizeTrack);
    const ids = new Set(normalized.map((track) => track.id));
    for (const [id, strip] of strips) {
      if (ids.has(id)) continue;
      disconnectAll(strip.nodes);
      strips.delete(id);
    }
    const anySoloed = normalized.some((track) => track.solo);
    normalized.forEach((track) => updateStrip(strips.get(track.id) || createStrip(track), track, anySoloed));
  };

  update(tracks, bpm);

  return {
    masterInput,
    trackInput: (trackId) => strips.get(trackId)?.input || null,
    update,
    analyser: (trackId) => strips.get(trackId)?.analyser || null,
    masterAnalyser,
    dispose() {
      for (const strip of strips.values()) disconnectAll(strip.nodes);
      strips.clear();
      disconnectAll(sharedNodes);
    },
  };
}

export default buildMixGraph;
